import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Video, ArrowLeft, ShieldCheck, AlertCircle } from "lucide-react";
import Logo from "@/components/app/Logo";
import { TeleconsultPaymentCard } from "@/components/app/TeleconsultPaymentCard";

export default function VideoRoom() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current) return;
    triedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/video/room/${appointmentId}`);
        setInfo(data);
      } catch (e) {
        setError(formatErr(e?.response?.data?.detail) || e.message);
      }
    })();
  }, [appointmentId]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <header className="border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-slate-800" data-testid="video-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Retour
        </Button>
        <div className="flex items-center gap-3">
          <Logo h={26} mono />
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 font-semibold">
            <ShieldCheck className="h-3 w-3" /> Salle privée & chiffrée
          </span>
        </div>
        <div className="w-24" />
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        {error ? (
          <div className="text-center max-w-md bg-slate-800 rounded-xl p-8 border border-slate-700">
            <AlertCircle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
            <div className="font-display text-xl font-bold">Salle indisponible</div>
            <p className="mt-3 text-slate-300 text-sm">{error}</p>
            <Button onClick={() => navigate("/dashboard/appointments")} className="mt-5 btn-primary" data-testid="video-back-appts-btn">
              Voir mes RDV
            </Button>
          </div>
        ) : info ? (
          info.status === "mocked" ? (
            <div className="text-center max-w-md">
              <div className="w-24 h-24 mx-auto rounded-full bg-[#FF7A5C]/20 flex items-center justify-center mb-4">
                <Video className="h-10 w-10 text-[#FF7A5C]" />
              </div>
              <div className="font-display text-2xl font-bold">Vidéo en mode démo</div>
              <p className="mt-3 text-slate-300">
                Daily.co n'est pas configuré. Une vraie salle vidéo sera créée automatiquement dès que la clé sera fournie.
              </p>
            </div>
          ) : (
            <iframe
              title="Daily Room"
              src={info.url}
              allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
              className="w-full h-full max-w-6xl rounded-lg border border-slate-700"
              style={{ minHeight: 620 }}
              data-testid="video-iframe"
            />
          )
        ) : (
          <div className="text-slate-400 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Préparation de votre salle sécurisée…
          </div>
        )}
      </div>

      <footer className="border-t border-slate-800 px-4 py-3 space-y-3">
        {info && <div className="max-w-3xl mx-auto"><TeleconsultPaymentCard appointmentId={appointmentId} dark /></div>}
        <div className="text-center text-xs text-slate-500">
          Cette téléconsultation est privée. Accès tracé pour conformité RGPD/HIPAA. Aucun enregistrement n'est effectué.
        </div>
      </footer>
    </div>
  );
}
